import React from 'react';
import { Stack, Tooltip, Typography } from '@mui/material';
import type { PipelineSourceField } from './types';

interface SourceFieldSelectorProps {
  fields: PipelineSourceField[];
  selectedFieldId?: string;
  onSelect: (field: PipelineSourceField) => void;
}

const SourceFieldSelector: React.FC<SourceFieldSelectorProps> = ({
  fields,
  selectedFieldId,
  onSelect,
}) => {
  if (fields.length === 0) {
    return (
      <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
        No source fields available
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      {fields.map((field) => {
        const isSelected = field.id === selectedFieldId;
        return (
          <Tooltip
            key={field.id}
            title={field.qualifiedName || `${field.sourceFile}.${field.sourceField}`}
            placement="right"
            arrow
          >
            <Stack
              direction="row"
              alignItems="center"
              spacing={0.75}
              onClick={() => onSelect(field)}
              sx={{
                cursor: 'pointer',
                px: 1,
                py: 0.375,
                borderRadius: 1,
                fontSize: '0.75rem',
                fontFamily: 'monospace',
                bgcolor: isSelected ? 'success.50' : 'transparent',
                border: '1px solid',
                borderColor: isSelected ? 'success.main' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  fontFamily: 'monospace',
                  fontSize: 11,
                  fontWeight: isSelected ? 700 : 400,
                  color: isSelected ? 'success.dark' : 'text.primary',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {field.sourceField}
              </Typography>
            </Stack>
          </Tooltip>
        );
      })}
    </Stack>
  );
};

export default SourceFieldSelector;
